import {
  Client,
  CoreKey,
  GraphRequest,
  GraphRequestCallback,
  MiddlewareOptions,
  ResponseType,
  Result,
  TypedGraphRequest
} from '@microsoft/microsoft-graph-client';
import { MutationFunction, useMutation } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useAPIQuery } from '../api/useAPIProvider';

interface GraphInformation {
  client: Client;
}

export const GraphContext = createContext<GraphInformation | null>(null);

declare module '@microsoft/microsoft-graph-client' {
  interface KeyValuePairObjectStringNumber {
    [key: string]: string | number;
  }

  // The only thing we're changing about the top level client is the api method
  // return our custom typed graph request instead of the default one.
  interface Client {
    /**
     * API
     *
     * @param path - The path string value
     * @returns A new GraphRequest instance
     * @example
     * ```
     * // GET https://graph.microsoft.com/v1.0/me/drive/root
     * client.api<DriveItem[]>('/me/drive/root').get();
     * ```
     */
    api<T extends Result>(path: string): TypedGraphRequest<T>;
  }

  type Result = Record<string, any> | Array<Record<string, any>>;
  type CoreType<T extends Result> = T extends Array<infer U> ? U : T;
  type CoreKey<T extends Result> = keyof CoreType<T>;
  type Response<T extends Result, S extends CoreKey<T> = CoreKey<T>> = T extends Array<any>
    ? GraphResponse<Pick<CoreType<T>, S>[]>
    : Pick<CoreType<T>, S>;

  class TypedGraphRequest<T extends Result = Result, S extends CoreKey<T> = CoreKey<T>> extends GraphRequest {
    expand(properties: string[]): TypedGraphRequest<T, S>;

    get(callback?: GraphRequestCallback): Promise<Response<T, S>>;
    post<B>(content: B, callback?: GraphRequestCallback): Promise<Response<T, S>>;
    create(content: any, callback?: GraphRequestCallback): Promise<Response<T, S>>;
    put(content: any, callback?: GraphRequestCallback): Promise<Response<T, S>>;
    patch(content: any, callback?: GraphRequestCallback): Promise<Response<T, S>>;
    update(content: any, callback?: GraphRequestCallback): Promise<Response<T, S>>;
    delete(callback?: GraphRequestCallback): Promise<Response<T, S>>;
    del(callback?: GraphRequestCallback): Promise<void>;
    select<E extends CoreKey<T>, X extends string>(select: (E | X)[]): TypedGraphRequest<T, E>;

    header(headerKey: string, headerValue: string): TypedGraphRequest<T, S>;
    headers(headers: KeyValuePairObjectStringNumber): TypedGraphRequest<T, S>;
    option(key: string, value: any): TypedGraphRequest<T, S>;
    options(options: { [key: string]: any }): TypedGraphRequest<T, S>;
    middlewareOptions(options: MiddlewareOptions[]): TypedGraphRequest<T, S>;
    version(version: string): TypedGraphRequest<T, S>;
    responseType(responseType: ResponseType): TypedGraphRequest<T, S>;
    orderby(properties: string | string[]): TypedGraphRequest<T, S>;
    filter(filterStr: string): TypedGraphRequest<T, S>;
    search(searchStr: string): TypedGraphRequest<T, S>;
    top(n: number): TypedGraphRequest<T, S>;
    skip(n: number): TypedGraphRequest<T, S>;
    skipToken(token: string): TypedGraphRequest<T, S>;
    count(isCount?: boolean): TypedGraphRequest<T, S>;
    query(queryDictionaryOrString: string | KeyValuePairObjectStringNumber): TypedGraphRequest<T, S>;
  }
}

export const useGraph = () => {
  const graphInformation = useContext(GraphContext);
  if (!graphInformation) {
    throw new Error('useGraph must be used within a GraphProvider');
  }
  return graphInformation;
};

export interface GraphResponse<T> {
  '@odata.context': string;
  value: T;
}

export function useGraphQuery<T>(key: (string | TypedGraphRequest)[], queryFn: () => Promise<T>) {
  const stableKey = useMemo(
    () =>
      key.map(k => {
        if (typeof k === 'string') return k;
        return getUniqueIdentifier(k);
      }),
    [key]
  );
  return useAPIQuery(stableKey, async () => queryFn());
}

export function useResource<T extends Result, S extends CoreKey<T> = CoreKey<T>>(
  fn: (client: Client) => TypedGraphRequest<T, S>
) {
  const { client } = useGraph();
  const resource = fn(client);

  return useGraphQuery([resource], async () => {
    return await resource.get();
  });
}

export function useGraphMutation<T, V = unknown>(func: MutationFunction<T, V>) {
  return useMutation<T, unknown, V>({
    mutationFn: func
  });
}

export const getUniqueIdentifier = (resource: TypedGraphRequest) => {
  // There is a private function that does this, but it's not exported
  // so we need to punch a hole in the type system to get it.

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wildWest = resource as any;
  if (!wildWest.buildFullUrl) throw new Error('buildFullUrl is not defined');
  const url = wildWest.buildFullUrl();
  return url as string;
};
